/**
 * AYU App Layout
 * Sidebar navigation (Desktop) + Bottom navigation (Mobile).
 * AYU Ayurveda Garden Design — Hell, frisch, natürlich.
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useLocation, Link, Redirect } from "wouter";
import BotanicalOrnament from "@/components/app/BotanicalOrnament";
import { DemoProvider, useDemo } from "@/contexts/DemoContext";
import DemoTimerBar from "@/components/DemoTimerBar";
import DemoExpiredOverlay from "@/components/DemoExpiredOverlay";
import DemoWrapper from "@/components/DemoWrapper";
import { useI18n } from "@/contexts/I18nContext";
import {
  LayoutDashboard,
  BookAudio,
  BookOpen,
  MessageCircle,
  User,
  LogOut,
  HelpCircle,
  Menu,
  X,
  Compass,
  Loader2,
  Users,
  FileText,
  Headphones,
  Microscope,
  UtensilsCrossed,
  Sun,
  ChefHat,
  Flame,
  Trophy,
  GraduationCap,
  Lock as LockIcon,
  Dna,
  ArrowUpRight,
  Shield,
  Watch,
  Moon,
  MessageSquarePlus,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useGamificationNotifier } from "@/hooks/useGamificationNotifier";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
// BugReportWidget entfernt — Feedback ist jetzt eine eigene Seite unter /feedback
import { OnboardingTour } from "@/components/OnboardingTour";

import { useCommunityUnread } from "@/hooks/useCommunityUnread";

interface NavItem {
  labelKey: string;
  mobileLabelKey?: string;
  href: string;
  icon: React.ReactNode;
  mobileShow?: boolean;
  premiumOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    labelKey: "nav.dashboard",
    href: "/",
    icon: <LayoutDashboard className="w-5 h-5" />,
    mobileShow: true,
  },
  {
    labelKey: "nav.tagebuch",
    href: "/tagebuch",
    icon: <BookOpen className="w-5 h-5" />,
    mobileShow: true,
  },
  {
    labelKey: "nav.coach",
    mobileLabelKey: "nav.coach",
    href: "/ayu-coach",
    icon: <MessageCircle className="w-5 h-5" />,
    mobileShow: true,
  },
  {
    labelKey: "nav.sessions",
    href: "/sessions",
    icon: <Headphones className="w-5 h-5" />,
  },
  {
    labelKey: "nav.hoerbuch",
    href: "/hoerbuch",
    icon: <BookAudio className="w-5 h-5" />,
  },
  {
    labelKey: "nav.kompass",
    href: "/kompass",
    icon: <Compass className="w-5 h-5" />,
    mobileShow: true,
  },
  {
    labelKey: "nav.community",
    href: "/community",
    icon: <Users className="w-5 h-5" />,
  },
  {
    labelKey: "nav.dokumente",
    href: "/dokumente",
    icon: <FileText className="w-5 h-5" />,
  },
  {
    labelKey: "nav.mikrobiom",
    href: "/mikrobiom",
    icon: <Microscope className="w-5 h-5" />,
    premiumOnly: true,
  },
  {
    labelKey: "nav.ernaehrung",
    href: "/ernaehrungsprofil",
    icon: <UtensilsCrossed className="w-5 h-5" />,
  },
  {
    labelKey: "nav.tagesroutine",
    href: "/tagesroutine",
    icon: <Sun className="w-5 h-5" />,
  },
  { labelKey: "nav.rezepte", href: "/rezepte", icon: <ChefHat className="w-5 h-5" /> },
  {
    labelKey: "nav.fortschritt",
    href: "/fortschritt",
    icon: <Flame className="w-5 h-5" />,
  },
  {
    labelKey: "nav.programme",
    href: "/programme",
    icon: <GraduationCap className="w-5 h-5" />,
  },
  {
    labelKey: "nav.gamification",
    href: "/gamification",
    icon: <Trophy className="w-5 h-5" />,
  },
  { labelKey: "nav.schlaf", href: "/schlaf", icon: <Moon className="w-5 h-5" /> },
  {
    labelKey: "nav.wearables",
    href: "/wearables",
    icon: <Watch className="w-5 h-5" />,
  },
  {
    labelKey: "nav.profil",
    href: "/profil",
    icon: <User className="w-5 h-5" />,
    mobileShow: true,
  },
  {
    labelKey: "nav.hilfe",
    href: "/hilfe",
    icon: <HelpCircle className="w-5 h-5" />,
  },
  {
    labelKey: "nav.feedback",
    href: "/feedback",
    icon: <MessageSquarePlus className="w-5 h-5" />,
  },
];

/* ─── Sidebar Upgrade Button (Basis → DNA) ─── v2 */
function SidebarUpgradeButton() {
  const { isDemo } = useDemo();
  const { t } = useI18n();
  // Im Demo-Modus keine Upgrade-Buttons anzeigen
  if (isDemo) return null;
  const [isLoading, setIsLoading] = useState(false);
  const upgradeMutation = trpc.stripe.createUpgradeCheckout.useMutation({
    onSuccess: data => {
      if (data.url) {
        toast.info(t("dashboard.upgradeRedirecting"));
        window.location.href = data.url;
      }
    },
    onError: err => {
      toast.error(err.message || t("dashboard.upgradeError"));
      setIsLoading(false);
    },
  });

  const handleUpgrade = () => {
    setIsLoading(true);
    upgradeMutation.mutate({ origin: window.location.origin });
  };

  return (
    <button
      onClick={handleUpgrade}
      disabled={isLoading || upgradeMutation.isPending}
      className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#d3ab5f] text-white text-[10px] font-medium hover:shadow-md hover:shadow-[#8b6914]/20 transition-all duration-200 disabled:opacity-50"
      title="Upgrade auf AYU + DNA Mikrobiom"
    >
      {isLoading || upgradeMutation.isPending ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : (
        <Dna className="w-3 h-3" />
      )}
      <span>{t("common.upgrade")}</span>
      <ArrowUpRight className="w-2.5 h-2.5" />
    </button>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { client, loading, isAuthenticated, logout } = useClientAuth();
  const { t } = useI18n();
  const [location] = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Body-Scroll blockieren wenn mobile Sidebar offen ist
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
    } else {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
    };
  }, [sidebarOpen]);

  // Gamification-Benachrichtigungen im Hintergrund
  useGamificationNotifier(client?.clientId);

  // Ungelesene Community-Nachrichten Badge
  const { unreadCount: communityUnread, markAllAsRead: markCommunityRead } =
    useCommunityUnread();

  // Mark community as read when user visits the community page
  useEffect(() => {
    if (location === "/community" || location.startsWith("/community/")) {
      markCommunityRead();
    }
  }, [location, markCommunityRead]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-[#6b7f4e]" />
          <span className="text-[#5a6b4a] text-sm">{t("common.loading")}</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    if (typeof window !== "undefined") {
      window.location.href = "/login";
      return null;
    }
    return <Redirect to="/login" />;
  }

  // Redirect to onboarding if not completed
  if (client && !client.onboardingComplete) {
    if (typeof window !== "undefined") {
      window.location.href = "/app/onboarding";
      return null;
    }
    return <Redirect to="/app/onboarding" />;
  }

  // Chat/audio pages: manage their own internal scroll — need overflow-hidden wrapper
  const FULLSCREEN_LOCKED_ROUTES = [
    "/tagebuch",
    "/ayu-coach",
    "/sessions",
    "/community",
    "/hilfe",
  ];
  // Content pages: need no max-w constraint but must be scrollable
  const FULLSCREEN_SCROLL_ROUTES = [
    "/hoerbuch",
    "/tagesroutine",
    "/rezepte",
    "/fortschritt",
    "/gamification",
    "/programme",
    "/wearables",
    "/feedback",
  ];
  const isFullscreenLocked = FULLSCREEN_LOCKED_ROUTES.some(
    r => location === r || location.startsWith(r + "/")
  );
  const isFullscreenScroll = FULLSCREEN_SCROLL_ROUTES.some(
    r => location === r || location.startsWith(r + "/")
  );
  // Admin braucht kein extra Padding/max-w (hat eigenes Layout)
  const isAdminPage = location === "/admin" || location.startsWith("/admin/");

  const isActive = (href: string) => {
    if (href === "/") return location === "/" || location === "";
    return location.startsWith(href);
  };

  return (
    <DemoProvider>
      <DemoTimerBar />
      <DemoExpiredOverlay />
      <DemoWrapper>
        <div className="min-h-screen lg:h-screen flex bg-[#f8f8f8] overflow-x-hidden relative">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex flex-col w-52 border-r border-[#d4dcc4] bg-[#eef2e6] relative">
            {/* Logo + Beta Badge */}
            <div className="border-b border-[#d4dcc4] flex items-center justify-center">
              <Link href="/" className="block">
                <img
                  src="/branding/logo-ayu-lg.svg"
                  alt="AYU"
                  className="h-fit w-40 object-contain py-2"
                />
              </Link>
            </div>

            {/* Welcome + Package Badge + Upgrade */}
            <div className="px-4 py-3 border-b border-[#d4dcc4]">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full overflow-hidden border border-[#C9A96E]/20 shadow-sm shrink-0">
                  {client?.avatarUrl ? (
                    <img
                      src={client.avatarUrl}
                      alt={client.firstName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#6b7f4e]/20 to-[#C9A96E]/20 flex items-center justify-center">
                      <span className="text-sm font-semibold text-[#6b7f4e]">
                        {(client?.firstName?.[0] || "").toUpperCase()}
                        {(client?.lastName?.[0] || "").toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <p
                    className="text-xs text-[#8a9680] italic"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {t("nav.willkommen")}
                  </p>
                  <p
                    className="text-[15px] font-semibold text-[#2d3a1e] mt-0"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {client?.firstName}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <span
                  className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-medium tracking-wide ${
                    client?.packageType === "premium"
                      ? "bg-[#c9a96e]/20 text-[#8b6914] border border-[#c9a96e]/40"
                      : "bg-[#6b7f4e]/15 text-[#4a5e38] border border-[#6b7f4e]/30"
                  }`}
                >
                  AYU {client?.packageType === "premium" ? t("common.premium") : t("common.basis")}
                </span>
                {client?.packageType !== "premium" && <SidebarUpgradeButton />}
              </div>
            </div>

            {/* Navigation — tout scrollable, logout à la fin */}
            <nav className="flex-1 py-3 px-2.5 space-y-0.5 overflow-y-auto">
              {NAV_ITEMS.map(item => {
                const isLockedForBasis =
                  item.premiumOnly && client?.packageType !== "premium";
                const isFeedback = item.href === "/feedback";
                const label = t(item.labelKey as any);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    data-tour={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] transition-all ${
                      isLockedForBasis
                        ? "text-[#b0b8a6] opacity-50 cursor-default"
                        : isFeedback
                          ? isActive(item.href)
                            ? "bg-[#C9A96E]/15 text-[#C9A96E] font-medium"
                            : "text-[#C9A96E] hover:bg-[#C9A96E]/8 hover:text-[#b0943e]"
                          : isActive(item.href)
                            ? "bg-[#6b7f4e]/15 text-[#6b7f4e] font-medium"
                            : "text-[#5a6b4a] hover:bg-[#6b7f4e]/8 hover:text-[#4a5e38]"
                    }`}
                  >
                    {isLockedForBasis ? (
                      <span className="w-5 h-5 flex items-center justify-center">
                        <LockIcon className="w-4 h-4 text-[#b0b8a6]" />
                      </span>
                    ) : (
                      item.icon
                    )}
                    {label}
                    {isLockedForBasis && (
                      <span className="ml-auto text-[10px] font-medium text-[#8b6914]/60 bg-[#8b6914]/10 px-1.5 py-0.5 rounded-full">
                        DNA
                      </span>
                    )}
                    {item.href === "/community" &&
                      communityUnread > 0 &&
                      !isActive(item.href) && (
                        <span className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold leading-none animate-in fade-in zoom-in duration-300">
                          {communityUnread > 99 ? "99+" : communityUnread}
                        </span>
                      )}
                  </Link>
                );
              })}

              {/* Admin-Link (nur für Admins) */}
              {client?.isAdmin && (
                <Link
                  href="/admin"
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] transition-all font-medium ${
                    isActive("/admin")
                      ? "bg-[#c9a96e]/15 text-[#8b6914]"
                      : "text-[#8b6914] hover:bg-[#c9a96e]/10 hover:text-[#6b5010]"
                  }`}
                >
                  <Shield className="w-4 h-4" />
                  {t("nav.admin")}
                </Link>
              )}

              {/* Logout — dernier élément */}
              <div className="pt-1 border-t border-[#d4dcc4]">
                <button
                  onClick={logout}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[12px] text-[#8a9680] hover:bg-red-50 hover:text-red-600 transition-all w-full"
                >
                  <LogOut className="w-4 h-4" />
                  {t("nav.logout")}
                </button>
              </div>
            </nav>
          </aside>

          {/* Mobile Header */}
          <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#eef2e6] border-b border-[#d4dcc4] px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-[#5a6b4a] hover:text-[#6b7f4e] p-1"
            >
              {sidebarOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
            <Link href="/" className="flex items-center gap-2">
              <img
                src="/branding/ayu-logo.svg"
                alt="AYU"
                className="w-8 h-8 object-contain"
              />
            </Link>
            {/* spacer to keep logo centered */}
            <div className="w-8" />
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 z-40"
              style={{ touchAction: "none" }}
            >
              <div
                className="absolute inset-0 bg-black/30"
                onClick={() => setSidebarOpen(false)}
                onTouchMove={e => e.preventDefault()}
              />
              <aside className="absolute top-0 left-0 w-full max-w-[22rem] h-full bg-[#eef2e6] border-r border-[#d4dcc4] pt-16 pb-0 flex flex-col overflow-hidden animate-in slide-in-from-left duration-300">
                {/* Welcome + Package Badge + Upgrade */}
                <div className="px-5 py-4 border-b border-[#d4dcc4]">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full overflow-hidden border border-[#C9A96E]/20 shadow-sm shrink-0">
                      {client?.avatarUrl ? (
                        <img
                          src={client.avatarUrl}
                          alt={client?.firstName || ""}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#6b7f4e]/20 to-[#C9A96E]/20 flex items-center justify-center">
                          <span className="text-xs font-semibold text-[#6b7f4e]">
                            {(client?.firstName?.[0] || "").toUpperCase()}
                            {(client?.lastName?.[0] || "").toUpperCase()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p
                        className="text-xs text-[#8a9680] italic"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {t("nav.willkommen")}
                      </p>
                      <p
                        className="text-lg font-semibold text-[#2d3a1e] mt-0.5"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {client?.firstName}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-[11px] font-medium tracking-wide ${
                        client?.packageType === "premium"
                          ? "bg-[#c9a96e]/20 text-[#8b6914] border border-[#c9a96e]/40"
                          : "bg-[#6b7f4e]/15 text-[#4a5e38] border border-[#6b7f4e]/30"
                      }`}
                    >
                      AYU{" "}
                      {client?.packageType === "premium" ? t("common.premium") : t("common.basis")}
                    </span>
                    {client?.packageType !== "premium" && (
                      <SidebarUpgradeButton />
                    )}
                  </div>
                </div>
                <nav
                  className="flex-1 py-4 px-3 space-y-1 overflow-y-auto overscroll-contain"
                  style={
                    {
                      touchAction: "pan-y",
                      WebkitOverflowScrolling: "touch",
                    } as React.CSSProperties
                  }
                >
                  {NAV_ITEMS.map(item => {
                    const isLockedForBasis =
                      item.premiumOnly && client?.packageType !== "premium";
                    const isFeedback = item.href === "/feedback";
                    const mobileLabel = item.mobileLabelKey ? t(item.mobileLabelKey as any) : t(item.labelKey as any);
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                          isLockedForBasis
                            ? "text-[#b0b8a6] opacity-50 cursor-default"
                            : isFeedback
                              ? isActive(item.href)
                                ? "bg-[#C9A96E]/15 text-[#C9A96E] font-medium"
                                : "text-[#C9A96E] hover:bg-[#C9A96E]/8 hover:text-[#b0943e]"
                              : isActive(item.href)
                                ? "bg-[#6b7f4e]/15 text-[#6b7f4e] font-medium"
                                : "text-[#5a6b4a] hover:bg-[#6b7f4e]/8 hover:text-[#4a5e38]"
                        }`}
                      >
                        {isLockedForBasis ? (
                          <span className="w-5 h-5 flex items-center justify-center">
                            <LockIcon className="w-4 h-4 text-[#b0b8a6]" />
                          </span>
                        ) : (
                          item.icon
                        )}
                        {mobileLabel}
                        {isLockedForBasis && (
                          <span className="ml-auto text-[10px] font-medium text-[#8b6914]/60 bg-[#8b6914]/10 px-1.5 py-0.5 rounded-full">
                            DNA
                          </span>
                        )}
                        {item.href === "/community" &&
                          communityUnread > 0 &&
                          !isActive(item.href) && (
                            <span className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold leading-none">
                              {communityUnread > 99 ? "99+" : communityUnread}
                            </span>
                          )}
                      </Link>
                    );
                  })}

                  {/* Admin-Link — Mobile */}
                  {client?.isAdmin && (
                    <Link
                      href="/admin"
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all font-medium ${
                        isActive("/admin")
                          ? "bg-[#c9a96e]/15 text-[#8b6914]"
                          : "text-[#8b6914] hover:bg-[#c9a96e]/10 hover:text-[#6b5010]"
                      }`}
                    >
                      <Shield className="w-5 h-5" />
                      {t("nav.admin")}
                    </Link>
                  )}

                  {/* Logout — dernier élément */}
                  <div className="pt-1 border-t border-[#d4dcc4]">
                    <button
                      onClick={() => {
                        logout();
                        setSidebarOpen(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#8a9680] hover:bg-red-50 hover:text-red-600 transition-all w-full"
                    >
                      <LogOut className="w-5 h-5" />
                      {t("nav.logout")}
                    </button>
                  </div>
                </nav>
              </aside>
            </div>
          )}

          {/* Mobile Bottom Navigation */}
          <nav
            className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#eef2e6] border-t border-[#d4dcc4] flex justify-around py-2 px-1"
            data-tour="bottom-nav"
          >
            {NAV_ITEMS.filter(i => i.mobileShow).map(item => {
              const mobileLabel = item.mobileLabelKey ? t(item.mobileLabelKey as any) : t(item.labelKey as any);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  data-tour={`nav-${mobileLabel.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-xs transition-all ${
                    isActive(item.href) ? "text-[#6b7f4e]" : "text-[#8a9680]"
                  }`}
                >
                  {item.icon}
                  <span className="text-[10px]">{mobileLabel}</span>
                </Link>
              );
            })}
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0 min-h-[100vh] pt-14 pb-20 lg:ml-52 lg:h-screen lg:overflow-y-auto lg:overscroll-contain lg:pt-0 lg:pb-0">
            {isFullscreenLocked ? (
              <div className="h-[calc(100dvh-56px-80px)] lg:h-full min-h-0 overflow-hidden">
                {children}
              </div>
            ) : isFullscreenScroll ? (
              <div>{children}</div>
            ) : isAdminPage ? (
              <div className="min-h-full">{children}</div>
            ) : (
              <div className="p-4 lg:p-8 max-w-5xl mx-auto">{children}</div>
            )}
          </main>

          {/* Onboarding Tour */}
          <OnboardingTour />
        </div>
      </DemoWrapper>
    </DemoProvider>
  );
}
